import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { getVideoUrl, isEmbedUrl, isHlsUrl, isValidVideoUrl } from '../utils/videoUtils';

// Import HLS.js - check if available at runtime
// If hls.js is not installed, this import will fail at build time
// For now, we'll use a try-catch approach with dynamic import in the effect
let Hls = null;


const OptimizedVideoPlayer = React.memo(({
  video,
  className = '',
  containerStyle = {},
  videoStyle = {},
  showControls = true,
  preload = 'metadata',
  playsInline = true,
  aspectRatio = 'aspect-video',
  poster = null
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const observerRef = useRef(null);
  const hlsRef = useRef(null);

  // refs to avoid state changes during scroll
  const isPlayingRef = useRef(false);
  const isObserverPausingRef = useRef(false);
  // small state for initial user-initiated play (only changes on user click)
  const [userPlayed, setUserPlayed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false); // Lazy loading state
  const [hlsError, setHlsError] = useState(false); // Track HLS loading errors
  const [videoError, setVideoError] = useState(false); // Track general video errors
  const [fallbackUrl] = useState(null); // Fallback to original video (setFallbackUrl unused for now)

  const videoUrl = useMemo(() => getVideoUrl(video), [video]);
  const isVideoValid = useMemo(() => isValidVideoUrl(video), [video]);
  const isEmbed = useMemo(() => isEmbedUrl(videoUrl), [videoUrl]);
  const isHls = useMemo(() => isHlsUrl(videoUrl), [videoUrl]);
  const posterStable = useMemo(() => poster || undefined, [poster]);
  
  // Check if video is still processing (invalid URL)
  const isProcessing = useMemo(() => {
    if (!video) return false;
    const videoStr = String(video).toLowerCase().trim();
    // Check for processing status indicators in the video value
    return videoStr.includes('pending') || 
           videoStr.includes('processing') || 
           videoStr.includes('uploading') ||
           videoStr === 'pending' ||
           videoStr === 'processing' ||
           videoStr === 'uploading' ||
           videoStr.endsWith('/pending') ||
           videoStr.endsWith('/processing') ||
           videoStr.endsWith('/uploading');
  }, [video]);
  
  // Extract newsId from HLS path for fallback: videos/{newsId}/playlist.m3u8
  const extractNewsIdFromPath = useCallback((url) => {
    if (!url) return null;
    const match = url.match(/videos\/([^/]+)\/playlist\.m3u8/);
    return match ? match[1] : null;
  }, []);
  
  // For HLS videos, don't set src directly (hls.js handles it)
  // For regular MP4 videos, use src with range request support (browser handles chunked loading)
  // Only load video src when shouldLoad is true (lazy loading)
  const videoSrc = useMemo(() => {
    // Don't load if video URL is invalid (still processing)
    if (!isVideoValid || isProcessing) return undefined;
    if (!shouldLoad) return undefined; // Lazy loading - don't load until near viewport
    if (isHls && !hlsError) {
      // HLS videos are handled by hls.js, don't set src (hls.js will attach media source)
      return undefined;
    }
    // If HLS failed, try fallback URL or original videoUrl
    if (isHls && hlsError && fallbackUrl) {
      return fallbackUrl;
    }
    // Regular MP4 videos use src - browser automatically uses range requests for chunked loading
    return videoUrl;
  }, [isHls, videoUrl, shouldLoad, hlsError, fallbackUrl, isVideoValid, isProcessing]);

  // minimal safe styles — avoid transforms on parent
  const baseContainerStyle = useMemo(() => ({
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#000',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    // Hardware acceleration for container
    willChange: 'contents',
    contain: 'layout style paint',
    ...containerStyle
  }), [containerStyle]);

  const baseVideoStyle = useMemo(() => ({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover', // try cover for smoother GPU compositing
    verticalAlign: 'top',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    // Hardware acceleration optimizations
    willChange: 'transform',
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
    transformStyle: 'preserve-3d',
    WebkitTransformStyle: 'preserve-3d',
    contain: 'layout style paint',
    isolation: 'isolate',
    imageRendering: 'auto',
    ...videoStyle
  }), [videoStyle]);

  // play/pause handlers use refs + DOM, only set React state on explicit user action
  const onPlay = useCallback(() => {
    isPlayingRef.current = true;
    // Update userPlayed state only if it's not already set (prevents unnecessary re-renders)
    if (!userPlayed) {
      setUserPlayed(true);
    }
    // Also update DOM directly for immediate feedback
    if (overlayRef.current) {
      overlayRef.current.style.opacity = '0';
      overlayRef.current.style.visibility = 'hidden';
      overlayRef.current.style.pointerEvents = 'none';
    }
  }, [userPlayed]);

  const onPause = useCallback(() => {
    isPlayingRef.current = false;
    if (overlayRef.current && !userPlayed) {
      overlayRef.current.style.opacity = '1';
      overlayRef.current.style.visibility = 'visible';
      overlayRef.current.style.pointerEvents = 'auto';
    }
    if (!isObserverPausingRef.current) {
      // only update userPlayed if the pause was user-initiated (optional)
      // we avoid setUserPlayed(false) here to prevent re-render on scroll pauses
    }
  }, [userPlayed]);

  // Lazy loading: Load video when near viewport (IntersectionObserver)
  useEffect(() => {
    if (!videoUrl || isEmbed || shouldLoad || !isVideoValid || isProcessing) return;
    
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Load video when it's within 500px of viewport (preload zone)
          if (entry.intersectionRatio > 0 || entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '500px', // Start loading 500px before entering viewport
        threshold: 0
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [videoUrl, isEmbed, shouldLoad, isVideoValid, isProcessing]);

  // Initialize HLS streaming for .m3u8 files (only when shouldLoad is true)
  useEffect(() => {
    if (!videoUrl || !videoRef.current || isEmbed || !isHls || !shouldLoad || !isVideoValid || isProcessing) return;
    
    const v = videoRef.current;
    
    // Dynamically import HLS.js if available
    const initHls = async () => {
      try {
        const hlsModule = await import('hls.js');
        const HlsClass = hlsModule.default || hlsModule;
        
        if (!HlsClass || !HlsClass.isSupported()) {
          // Fallback to native HLS (Safari)
          if (v.canPlayType('application/vnd.apple.mpegurl')) {
            v.src = videoUrl;
          }
          return;
        }
        
        // Clean up existing HLS instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        
        // Create new HLS instance with adaptive bitrate streaming configuration
        const hls = new HlsClass({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          maxBufferSize: 60 * 1000 * 1000, // 60MB
          maxBufferHole: 0.5,
          highBufferWatchdogPeriod: 2,
          nudgeOffset: 0.1,
          nudgeMaxRetry: 3,
          maxFragLoadingTimeOut: 200000,
          fragLoadingTimeOut: 20000,
          manifestLoadingTimeOut: 10000,
          levelLoadingTimeOut: 10000,
          // Adaptive bitrate configuration
          abrEwmaDefaultEstimate: 500000, // 500kbps initial estimate
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,
          abrMaxWithRealBitrate: false,
          maxStarvationDelay: 4,
          maxLoadingDelay: 4,
          minAutoBitrate: 0,
          emeEnabled: false,
          widevineLicenseUrl: undefined,
          // Chunked loading configuration
          startLevel: -1, // Auto-select initial quality
          capLevelToPlayerSize: true, // Cap quality to player size
          // Performance optimizations
          progressive: false,
          debug: false
        });

        hlsRef.current = hls;

        // Load the source
        hls.loadSource(videoUrl);
        hls.attachMedia(v);

        // Handle HLS events
        hls.on(HlsClass.Events.MANIFEST_PARSED, () => {
          // Manifest parsed successfully, video is ready
          console.debug('HLS manifest parsed, video ready');
        });

        hls.on(HlsClass.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case HlsClass.ErrorTypes.NETWORK_ERROR:
                console.error('HLS network error, attempting recovery');
                // Check if it's a 404 (file not found)
                if (data.details === 'manifestLoadError' || data.response?.code === 404 || data.fatal) {
                  console.warn('HLS playlist not found, video processing may have failed');
                  setHlsError(true);
                  setVideoError(true);
                  // Try to find original video file
                  const newsId = extractNewsIdFromPath(videoUrl);
                  if (newsId) {
                    console.warn(`HLS file missing for newsId: ${newsId}. Original video file may still exist.`);
                  }
                  if (hlsRef.current) {
                    hlsRef.current.destroy();
                    hlsRef.current = null;
                  }
                  return;
                }
                hls.startLoad();
                break;
              case HlsClass.ErrorTypes.MEDIA_ERROR:
                console.error('HLS media error, attempting recovery');
                hls.recoverMediaError();
                break;
              default:
                console.error('HLS fatal error, destroying instance');
                hls.destroy();
                break;
            }
          }
        });
      } catch (error) {
        // hls.js not available or failed to load - use native playback
        console.debug('HLS.js not available, using native video playback', error);
        if (v.canPlayType('application/vnd.apple.mpegurl')) {
          v.src = videoUrl;
        }
      }
    };

    initHls();

    // Cleanup function
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, isEmbed, isHls, shouldLoad, extractNewsIdFromPath, isVideoValid, isProcessing]);

  // IntersectionObserver — pause when out of view, never auto-play
  // This observer only manages play/pause, not loading (separate observer handles loading)
  useEffect(() => {
    if (!videoUrl || !videoRef.current || isEmbed || !shouldLoad || !isVideoValid || isProcessing) return;
    const v = videoRef.current;
    const container = containerRef.current;
    if (!container) return;

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Use requestIdleCallback or setTimeout to avoid blocking scroll
    const handler = (entries) => {
      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          const isVisible = entry.intersectionRatio >= 0.1;
          
          // Only pause when < 10% visible to avoid jitter
          // Use refs to avoid state updates during scroll
          if (!isVisible && !v.paused && isPlayingRef.current) {
            isObserverPausingRef.current = true;
            v.pause();
            isObserverPausingRef.current = false;
          }
        });
      });
    };

    observerRef.current = new IntersectionObserver(handler, {
      threshold: [0, 0.1, 0.5, 1.0], // Multiple thresholds for smoother transitions
      rootMargin: '200px' // Pause when 200px out of viewport
    });

    observerRef.current.observe(container);

    // Add event listeners for play/pause tracking
    v.addEventListener('play', onPlay, { passive: true });
    v.addEventListener('pause', onPause, { passive: true });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [videoUrl, isEmbed, shouldLoad, onPlay, onPause, isVideoValid, isProcessing]);

  const handlePlayClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (!isPlayingRef.current) {
      v.play().then(() => {
        // update userPlayed to reflect explicit user action (safe to re-render once)
        setUserPlayed(true);
      }).catch(err => {
        console.debug('play() failure', err);
      });
    } else {
      // allow native pause via controls
      v.pause();
    }
  }, []);

  // Memoize overlay styles before early return (hooks must be called unconditionally)
  const overlayStyle = useMemo(() => ({
    backgroundColor: 'rgba(0,0,0,0.35)',
    transition: 'opacity 180ms ease, visibility 180ms',
    opacity: userPlayed ? 0 : 1, // Use state for initial render, ref for DOM updates
    visibility: userPlayed ? 'hidden' : 'visible',
    pointerEvents: userPlayed ? 'none' : 'auto',
  }), [userPlayed]);

  const playButtonStyle = useMemo(() => ({
    background: 'var(--app-card)',
    color: 'var(--app-text)',
    border: '1px solid var(--app-border)',
    borderRadius: 9999,
    padding: 12,
    boxShadow: '0 6px 18px var(--app-shadow)',
    transform: 'translateZ(0)',
  }), []);

  // Show processing message if video URL is invalid (still processing)
  // Check this FIRST before checking videoUrl, because getVideoUrl returns null for invalid videos
  // Also check if videoUrl is null but we have a video prop (means it's invalid/processing)
  const shouldShowProcessing = !isVideoValid || isProcessing || (video && !videoUrl && !isEmbed);
  
  if (shouldShowProcessing) {
    return (
      <div ref={containerRef} className={`relative w-full ${className}`} style={baseContainerStyle}>
        <div className="flex flex-col items-center justify-center p-8 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <svg className="w-16 h-16 text-blue-500 mb-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Video Processing</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
            This video is currently being processed. Please check back in a few moments.
          </p>
        </div>
      </div>
    );
  }

  // If video is valid but videoUrl is null (shouldn't happen, but safety check)
  if (!videoUrl) return null;

  // Show error message if video failed to load
  if (videoError && !isEmbed) {
    const isWebM = videoUrl && videoUrl.toLowerCase().includes('.webm');
    return (
      <div ref={containerRef} className={`relative w-full ${className}`} style={baseContainerStyle}>
        <div className="flex flex-col items-center justify-center p-8 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Video Not Available</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md mb-4">
            {videoUrl && (videoUrl.toLowerCase().includes('/pending') || videoUrl.toLowerCase().includes('pending'))
              ? "This video is still being processed. Please check back in a few moments."
              : isHls 
              ? "The video file was not processed correctly. Please contact the content creator or try again later."
              : isWebM
              ? "WebM format is not supported by your browser (especially Safari/iOS). This video needs to be converted to MP4 format for better compatibility."
              : "Unable to load the video. The file may have been removed or is unavailable."}
          </p>
          {isWebM && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg max-w-md">
              <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
                <strong>Note:</strong> This is an older WebM video file. For best compatibility:
              </p>
              <ul className="text-xs text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Use Chrome, Firefox, or Edge browser</li>
                <li>Or contact the content creator to re-upload the video</li>
                <li>New videos are automatically converted to MP4 format</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isEmbed) {
    const embedUrl = videoUrl.includes('autoplay=1')
      ? videoUrl.replace('autoplay=1', 'autoplay=0')
      : videoUrl.includes('?')
        ? `${videoUrl}&autoplay=0`
        : `${videoUrl}?autoplay=0`;

    return (
      <div ref={containerRef} className={`relative w-full ${className}`} style={baseContainerStyle}>
        <iframe
          src={embedUrl}
          className={`w-full ${aspectRatio} rounded-lg`.trim()}
          style={{ width: '100%', height: '100%', border: 0 }}
          frameBorder="0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video player"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative w-full ${className}`} style={baseContainerStyle}>
      <video
        ref={videoRef}
        src={videoSrc}
        controls={showControls}
        className={`w-full ${aspectRatio} rounded-lg`.trim()}
        style={baseVideoStyle}
        playsInline={playsInline}
        preload={preload}
        poster={posterStable}
        onError={(e) => {
          const videoElement = e.target;
          const error = videoElement.error;
          
          // Get detailed error information
          let errorMessage = 'Unknown video error';
          let errorCode = null;
          
          if (error) {
            errorCode = error.code;
            switch (error.code) {
              case 1: // MEDIA_ERR_ABORTED
                errorMessage = 'Video loading aborted';
                break;
              case 2: // MEDIA_ERR_NETWORK
                errorMessage = 'Network error while loading video';
                break;
              case 3: // MEDIA_ERR_DECODE
                errorMessage = 'Video decoding error';
                break;
              case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                errorMessage = 'Video format not supported';
                // Check if it's a WebM file and browser doesn't support it
                if (videoUrl && videoUrl.toLowerCase().includes('.webm')) {
                  const video = videoElement;
                  const supportsWebM = video.canPlayType('video/webm; codecs="vp8, vorbis"') || 
                                       video.canPlayType('video/webm; codecs="vp9, opus"') ||
                                       video.canPlayType('video/webm');
                  if (!supportsWebM) {
                    errorMessage = 'WebM format not supported by your browser. Please use Chrome, Firefox, or Edge to view this video.';
                  }
                }
                break;
              default:
                errorMessage = `Video error (code: ${error.code})`;
            }
          }
          
          // Check if URL contains pending/processing indicators
          const isPendingUrl = videoUrl && (
            videoUrl.toLowerCase().includes('/pending') ||
            videoUrl.toLowerCase().includes('/processing') ||
            videoUrl.toLowerCase().includes('/uploading') ||
            videoUrl.toLowerCase().endsWith('pending') ||
            videoUrl.toLowerCase().endsWith('processing') ||
            videoUrl.toLowerCase().endsWith('uploading')
          );
          
          // Check if it's a WebM format issue (we'll show user-friendly message, so reduce console noise)
          const isWebMFormatIssue = errorCode === 4 && videoUrl && videoUrl.toLowerCase().includes('.webm');
          
          if (isPendingUrl) {
            // Don't log pending URLs as errors - they're expected during processing
            console.debug('Video is still processing:', { videoUrl });
            errorMessage = 'Video is still being processed. Please check back in a few moments.';
          } else if (!isWebMFormatIssue) {
            // Only log non-WebM errors to console (WebM errors are expected and handled gracefully)
            console.error('Video error details:', {
              errorCode,
              errorMessage,
              videoSrc: videoElement.src,
              videoUrl,
              isHls,
              networkState: videoElement.networkState,
              readyState: videoElement.readyState,
              canPlayWebM: videoElement.canPlayType('video/webm'),
              canPlayMP4: videoElement.canPlayType('video/mp4')
            });
          } else {
            // Log WebM format issue at debug level (less noisy)
            console.debug('WebM format not supported by browser:', {
              videoUrl,
              canPlayWebM: videoElement.canPlayType('video/webm'),
              canPlayMP4: videoElement.canPlayType('video/mp4'),
              userAgent: navigator.userAgent
            });
          }
          
          setVideoError(true);
          
          // If HLS video failed to load, try to find original video file
          if (isHls && !hlsError) {
            setHlsError(true);
            const newsId = extractNewsIdFromPath(videoUrl);
            if (newsId) {
              console.warn(`HLS video failed to load for newsId: ${newsId}. Error: ${errorMessage}`);
              console.warn('This usually means the HLS playlist file was not created during video processing.');
            }
          }
          
          // Fallback for HLS errors
          if (isHls && hlsRef.current && !hlsError) {
            const hls = hlsRef.current;
            if (hls.media && hls.media.error) {
              hls.recoverMediaError();
            }
          }
        }}
        onClick={(e) => {
          // only trigger play on initial click
          if (!isPlayingRef.current) handlePlayClick(e);
        }}
        muted={false}
        // Enable range requests for chunked loading (browser default for MP4)
        // HLS videos are handled by hls.js with adaptive bitrate streaming
        // do NOT set key prop — avoid remounts
      >
        Your browser does not support the video tag.
      </video>

      {/* overlay controlled by opacity & visibility (no display toggles) */}
      <div
        ref={overlayRef}
        onClick={handlePlayClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePlayClick(e); }}
        className="absolute inset-0 flex items-center justify-center z-10 rounded-lg"
        style={overlayStyle}
      >
        <div style={playButtonStyle}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
}, (prev, next) => {
  // Comprehensive comparison to prevent unnecessary re-renders
  // Only re-render when core props actually change
  if (prev.video !== next.video) return false;
  if (prev.className !== next.className) return false;
  if (prev.showControls !== next.showControls) return false;
  if (prev.preload !== next.preload) return false;
  if (prev.playsInline !== next.playsInline) return false;
  if (prev.aspectRatio !== next.aspectRatio) return false;
  if (prev.poster !== next.poster) return false;
  
  // For style objects, use reference equality (if same reference, they're equal)
  // This prevents re-renders when parent passes new empty objects
  if (prev.containerStyle !== next.containerStyle) {
    // Only re-render if styles actually changed (shallow comparison)
    const prevKeys = Object.keys(prev.containerStyle || {});
    const nextKeys = Object.keys(next.containerStyle || {});
    if (prevKeys.length !== nextKeys.length) return false;
    for (const key of prevKeys) {
      if (prev.containerStyle[key] !== next.containerStyle[key]) return false;
    }
  }
  
  if (prev.videoStyle !== next.videoStyle) {
    const prevKeys = Object.keys(prev.videoStyle || {});
    const nextKeys = Object.keys(next.videoStyle || {});
    if (prevKeys.length !== nextKeys.length) return false;
    for (const key of prevKeys) {
      if (prev.videoStyle[key] !== next.videoStyle[key]) return false;
    }
  }
  
  return true; // Props are equal, skip re-render
});

OptimizedVideoPlayer.displayName = 'OptimizedVideoPlayer';
export default OptimizedVideoPlayer;
